import { useEffect, useRef } from 'react';

// Detect "desktop": a device with a precise pointer (mouse/trackpad) and hover
// support. Touch devices (phones/tablets) are excluded so we never auto-open a
// selector or pop the keyboard on entry, which is annoying on mobile.
function isDesktopView(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

// First focusable field inside the form: react-select inputs (SelectField),
// MUI text inputs, textareas, native selects and MUI selects.
const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[class*="MuiSelect-root"]',
].join(',');

/**
 * Returns a ref to attach to a <form> (or any container). When `ready` becomes
 * true, it focuses the first focusable field — but only on desktop.
 *
 * This auto-detects the first *visible* field, which is what we want for
 * VenueEditor screens where the venue selector is hidden.
 *
 * @param ready Whether the form is actually mounted/visible (e.g. !isLoading).
 */
export default function useAutoFocusFirstField<T extends HTMLElement = HTMLFormElement>(ready = true) {
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    if (!ready || !isDesktopView()) return;

    const container = containerRef.current;
    if (!container) return;

    const candidates = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.getAttribute('aria-disabled') !== 'true',
    );

    candidates[0]?.focus();
  }, [ready]);

  return containerRef;
}
